import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Logo from '../components/Logo';
import ClientsMarquee from '../components/ClientsMarquee';
import StackingCards from '../components/StackingCards';
import ServicesAccordion from '../components/ServicesAccordion';
import FAQ from '../components/FAQ';
import HubSpotForm from '../components/HubSpotForm';
import SEO from '../components/SEO';

const Home = () => {
  const { t, i18n } = useTranslation();

  // SEO data with translations
  const seoData = {
    fr: {
      title: 'FOMOW | Agence Performance Commerciale B2B - Accélération des Revenus',
      description: 'FOMOW génère des rendez-vous qualifiés et accompagne les dirigeants B2B dans la structuration de leur développement commercial. +10M€ d\'opportunités générées pour nos clients.'
    },
    en: {
      title: 'FOMOW | B2B Commercial Performance Agency - Revenue Acceleration',
      description: 'FOMOW generates qualified meetings and supports B2B executives in structuring their commercial development. +€10M in opportunities generated for our clients.'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.fr;

  // Structured data for homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": currentSeo.title,
    "description": currentSeo.description,
    "url": "https://www.fomow.fr/",
    "mainEntity": {
      "@type": "Organization",
      "name": "FOMOW",
      "description": "Agence de performance commerciale B2B"
    }
  };

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        path="/"
        structuredData={structuredData}
      />
      <Navbar />
      
      {/* Hero Section - Thème clair avec bleu FOMOW */}
      <section className="relative min-h-[100svh] flex flex-col justify-center px-4 sm:px-6 pt-24 sm:pt-28 pb-10 sm:pb-14 overflow-hidden bg-goudron">
        {/* Subtle background gradients */}
        <div className="absolute top-20 left-10 w-48 sm:w-96 h-48 sm:h-96 bg-[#2A41E5]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-64 sm:w-[500px] h-64 sm:h-[500px] bg-[#2A41E5]/5 rounded-full blur-3xl" />

        <div className="relative z-10 text-center max-w-5xl mx-auto w-full px-4 sm:px-6">
          {/* Badge */}
          <div className="inline-flex items-center px-3 sm:px-5 py-2 sm:py-2.5 rounded-full bg-white shadow-soft border border-gray-100 mb-6 sm:mb-10">
            <div className="w-2 h-2 bg-[#2A41E5] rounded-full mr-2 sm:mr-3 animate-pulse" />
            <span className="text-xs sm:text-sm text-[#B8860B] font-medium">{t('home.badge')}</span>
          </div>

          {/* Main Title */}
          <h1 className="text-[1.75rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-5 sm:mb-10 leading-[1.2] sm:leading-tight">
            {t('home.title')}
            <br />
            <span className="text-[#2A41E5]">{t('home.titleHighlight')}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-[0.95rem] sm:text-xl md:text-2xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed">
            {t('home.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 mb-10 sm:mb-20 px-2 sm:px-4">
            <a
              href="#services-accordion"
              className="group w-full sm:w-auto px-6 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-[#2A41E5] text-white font-bold text-sm sm:text-base uppercase tracking-wider hover:shadow-xl hover:shadow-[#2A41E5]/30 transition-all duration-300 flex items-center justify-center hover:bg-[#1E32B8]"
            >
              {t('home.ctaPrimary')}
              <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2 sm:ml-3 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            
            <a
              href="#contact"
              className="w-full sm:w-auto px-6 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-white shadow-lg border border-gray-200 text-gray-700 font-bold text-sm sm:text-base uppercase tracking-wider hover:shadow-xl hover:border-gray-300 transition-all duration-300 text-center"
            >
              {t('home.ctaSecondary')}
            </a>
          </div>

          {/* Bande logos clients défilante - dans le Hero */}
          <div className="mt-8 sm:mt-12">
            <ClientsMarquee />
          </div>
        </div>

        {/* Scroll indicator - hidden on mobile */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Section About - Qui sommes-nous */}
      <StackingCards />

      {/* Section Services Accordion - On fait le nécessaire */}
      <ServicesAccordion />

      {/* Section FAQ - Optimisée AEO */}
      <FAQ />

      {/* Section Contact - Formulaire HubSpot */}
      <HubSpotForm />

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">&copy; 2024 FOMOW. {t('common.allRightsReserved')}.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
